import {
  GetProductById,
  GetProductsByBatch,
  GetVariantById,
  GetVariantsByBatch,
  GetProductsByCollectionId,
  GetCollectionById,
  GetCollectionVariantsFromMetafield,
  GetMetaobjectById,
  GetCollectionsByBatch,
  GetCollectionsByTitles,
  SearchProducts,
} from 'lib-queries'
import {
  isStringifiedArray,
  convertKeysToCamelCase,
  stripHtml,
} from 'lib-utils'

/**
 * A simple client for working with the Storefront GraphQL API.
 */
class StorefrontClient {
  /**
   * @param {string} domain - The domain of the Shopify store.
   * @param {string} accessToken - The access token for the Storefront API.
   * @param {string} apiVersion - The API version to use.
   */
  constructor(
    domain = __THEME_GLOBALS__.domain,
    accessToken = __THEME_GLOBALS__.storefrontToken,
    apiVersion = '2024-01'
  ) {
    this.endpoint = `https://${domain}/api/${apiVersion}/graphql.json`
    this.headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': accessToken,
    }
  }

  /**
   * Execute a GraphQL query against the endpoint.
   * @param {string} graphQLQuery - The GraphQL query to be executed.
   * @param {Object} variables - The variables for the query.
   * @returns {Promise<Object>} The JSON response from the endpoint.
   */
  async query(graphQLQuery, variables = {}) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          query: graphQLQuery,
          variables,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(`Shopify GraphQL Error: ${JSON.stringify(errorData)}`)
      }

      return await response.json()
    } catch (error) {
      console.error('An error occurred while querying Shopify:', error)
      throw error
    }
  }

  /**
   * Fetch product data by its ID.
   * @param {string} id - The product ID.
   * @returns {Promise<Object>} The processed product data.
   */
  async getProductById(id) {
    const response = await this.query(GetProductById, {
      id: `gid://shopify/Product/${id}`,
    })
    return cleanUpProductData(response.data)
  }

  /**
   * Fetch batch of products data by their IDs.
   * @param {string} ids - The product IDs.
   * @returns {Promise<Object>} The processed products data.
   */
  async getProductsByBatch(ids) {
    const response = await this.query(GetProductsByBatch, {
      ids: ids.map((id) => `gid://shopify/Product/${id}`),
    })
    return response.data.nodes
      .filter(Boolean)
      .map((product) => cleanUpProductData({ node: product }))
  }

  /**
   * Fetch variant data by its ID.
   * @param {string} id - The variant ID.
   * @returns {Promise<Object>} The processed variant data.
   */
  async getVariantById(id) {
    const response = await this.query(GetVariantById, {
      id: `gid://shopify/ProductVariant/${id}`,
    })
    return cleanUpVariantData(response.data)
  }

  /**
   * Fetch batch of variant data by their IDs.
   * @param {string} ids - The variant IDs.
   * @returns {Promise<Object>} The processed variants data.
   */
  async getVariantsByBatch(ids) {
    const response = await this.query(GetVariantsByBatch, {
      ids: ids.map((id) => `gid://shopify/ProductVariant/${id}`),
    })
    return response.data.nodes
      .filter(Boolean)
      .map((variant) => cleanUpVariantData({ node: variant }))
  }

  /**
   * Fetch collection data by its ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch
   * @returns {Promise<Object>} The processed collection data.
   */
  async getCollectionById(id, limit) {
    const response = await this.query(GetCollectionById, {
      id: `gid://shopify/Collection/${id}`,
      limit,
    })
    return cleanUpCollectionData(response.data)
  }

  /**
   * Fetch collection data by its ID using variants from override metafield instead of collection products
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch
   * @returns {Promise<Object>} The processed collection data.
   */
  async getCollectionVariantsFromMetafield(id) {
    const response = await this.query(GetCollectionVariantsFromMetafield, {
      id: `gid://shopify/Collection/${id}`,
    })
    return cleanUpCollectionVariantsFromMetafieldData(response.data)
  }

  /**
   * Fetch metaobject data by its ID.
   * @param {string} id - The metaobject ID.
   * @returns {Promise<Object>} The processed metaobject data.
   */
  async getMetaobjectById(id) {
    const response = await this.query(GetMetaobjectById, {
      id: `gid://shopify/Metaobject/${id}`,
    })
    return cleanUpMetaobjectData(response.data)
  }

  /**
   * Fetch products only based on collection ID.
   * @param {string} id - The collection ID.
   * @param {number} limit - The max number of products in the collection to fetch.
   * @returns {Promise<Array>} All products for the collection.
   */
  async getProductsByCollectionId(id, limit) {
    const allProducts = []
    const maxRequests = 10
    let requestCount = 0
    const fetchPage = async (id, limit, cursor) => {
      if (requestCount >= maxRequests) {
        return
      }
      const params = {
        id: `gid://shopify/Collection/${id}`,
        limit,
      }
      if (cursor) params.afterCursor = cursor
      const response = await this.query(GetProductsByCollectionId, params)
      const products = cleanUpCollectionData(response.data).products
      allProducts.push(...products)
      const pageInfo = response.data.node.products.pageInfo
      if (pageInfo.hasNextPage) {
        requestCount++
        await fetchPage(id, limit, pageInfo.endCursor)
      }
    }
    await fetchPage(id, limit)
    return allProducts
  }

  /**
   * Fetch batch of collection data by their IDs.
   * @param {string} ids - An array of the collection IDs.
   * @returns {Promise<Object>} The processed collections data.
   */
  async getCollectionsByBatch(ids) {
    const response = await this.query(GetCollectionsByBatch, {
      ids: ids.map((id) => `gid://shopify/Collection/${id}`),
    })
    return response.data.nodes
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData({ node: collection }))
  }

  /**
   * Fetch collections based on an array of titles.
   *
   * @function GetCollectionsByTitles
   * @param {string[]} titles - An array of collection titles to search for.
   * @returns {object[]} An array of collections matching the provided titles.
   */
  async getCollectionsByTitles(titles) {
    const titlesQuery = titles.map((title) => `title:${title}`).join(' OR ')
    const response = await this.query(GetCollectionsByTitles, {
      titlesQuery,
    })
    return response.data.collections.edges
      .filter(Boolean)
      .map((collection) => cleanUpCollectionData(collection))
  }

  /**
   * Search products.
   * @param {string} query - The search query.
   * @returns {Promise<Array<Object>>} The list of matched products.
   */
  async searchProducts(query, limit = 250) {
    const response = await this.query(SearchProducts, { query, limit })
    return cleanUpSearchData(response.data)
  }
}

const normalizePrice = (price) => {
  return (Number(price) * 100).toFixed(0)
}

/**
 * Fetches the product recommendations for a given product.
 *
 * @param {string} productId - The product ID.
 * @param {number} limit - The max number of products to fetch
 * @param {string} intent - The recommendation intent.
 * @returns {Promise<Object>} The processed product data.
 */
const getProductRecommendations = async (
  productId,
  limit = 10,
  intent = 'related'
) => {
  const response = await fetch(
    `${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`
  )
  const { products } = await response.json()
  return products
}

/**
 * Strips GraphQL Global ID (GID) down to its numeric component.
 * @param {string} value - The GID.
 * @returns {string} The stripped GID.
 */
function stripGID(value) {
  const gidPattern = /^gid:\/\/shopify\/\w+\//
  if (gidPattern.test(value)) {
    return parseInt(value.replace(gidPattern, ''))
  }
  return value
}

/**
 * Makes metafield data from GraphQL workable with JS.
 * @param {Array<Object>} rawMetafields - The GraphQL response for metafields.
 * @returns {Object} The processed metafield data.
 */
function cleanUpMetafields(rawMetafields) {
  if (!rawMetafields) return {}
  const result = rawMetafields
    .filter((m) => m !== null)
    .reduce((acc, metafield) => {
      if (!acc[metafield.namespace]) {
        acc[metafield.namespace] = {}
      }
      let value
      if (Object.keys(metafield.reference || {}).includes('image')) {
        value = metafield.reference.image.originalSrc
      } else if (Object.keys(metafield.reference || {}).includes('url')) {
        value = metafield.reference.url
      } else if (isStringifiedArray(metafield.value)) {
        const array = JSON.parse(metafield.value)
        value = array.map((i) => stripGID(i))
      } else if (metafield.reference) {
        value = stripGID(metafield.value)
      } else if (metafield.value === 'true') {
        value = true
      } else if (metafield.value === 'false') {
        value = false
      } else {
        value = metafield.value
      }
      acc[metafield.namespace][metafield.key] = value
      return acc
    }, {})
  return convertKeysToCamelCase(result)
}

/**
 * Makes product data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for product.
 * @returns {Object|null} The processed product data.
 */
function cleanUpProductData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawProduct = rawData.node

  const variantsSortedByPrice =
    rawProduct.variants?.edges.sort((a, b) => {
      const priceA = parseFloat(a.node.priceV2.amount)
      const priceB = parseFloat(a.node.priceV2.amount)
      if (priceA < priceB) return -1
      if (priceA > priceB) return 1
      return 0
    }) || []
  const lowestPriceVariant = variantsSortedByPrice[0]
  let compareAtPrice = null
  if (variantsSortedByPrice.length) {
    const compareAtPriceProperty =
      lowestPriceVariant.node.compareAtPriceV2?.amount
    if (compareAtPriceProperty) compareAtPrice = Number(compareAtPriceProperty)
  }

  const metafields = cleanUpMetafields(rawProduct.metafields)
  const rawMinPrice = rawProduct.priceRange?.minVariantPrice?.amount
  const rawMaxPrice = rawProduct.priceRange?.minVariantPrice?.amount
  const rawBadgeData = metafields?.customFields?.cardBadge
  let badgeText = ''
  if (typeof rawBadgeData === 'object') {
    badgeText = rawBadgeData[0] // Only render first badge in card
  } else {
    badgeText = rawBadgeData
  }
  if (badgeText) {
    badgeText = badgeText.split('|')[0].trim() // Only show first if there are multiple fragments
  }

  return {
    id: stripGID(rawProduct.id),
    available: rawProduct.availableForSale,
    title: rawProduct.title,
    description: rawProduct.descriptionHtml,
    tagLine: stripHtml(metafields?.customFields?.productTagline),
    badgeText: badgeText,
    hideReviews: metafields?.custom?.hideReviews || false,
    minPrice: normalizePrice(rawMinPrice),
    maxPrice: normalizePrice(rawMinPrice),
    compareAtPrice: normalizePrice(compareAtPrice),
    handle: rawProduct.handle,
    type: rawProduct.productType,
    vendor: rawProduct.vendor,
    createdAt: new Date(rawProduct.createdAt),
    updatedAt: new Date(rawProduct.updatedAt),
    publishedAt: new Date(rawProduct.publishedAt),
    url: rawProduct.onlineStoreUrl,
    tags: rawProduct.tags,
    featuredImage: rawProduct.featuredImage,
    images: rawProduct.images?.edges.map((edge) => edge.node),
    options: rawProduct.options,
    swatchValues: rawProduct.options.reduce((acc, current) => {
      if (['color', 'material'].includes(current.name.toLowerCase())) {
        return [...acc, ...current.values]
      }
      return acc
    }, []),
    collections: rawProduct.collections?.edges.map((edge) => {
      return cleanUpCollectionData(edge)
    }),
    variants: rawProduct.variants?.edges.map((edge) => {
      const id = stripGID(edge.node.id)
      return {
        id,
        title: edge.node.title,
        available: edge.node.availableForSale,
        price: normalizePrice(edge.node.priceV2.amount),
        compareAtPrice: normalizePrice(edge.node.compareAtPriceV2?.amount),
        currencyCode: edge.node.priceV2.currencyCode,
        availableForSale: edge.node.availableForSale,
        sku: edge.node.sku,
        image: { src: edge.node?.image?.url },
        options: edge.node.selectedOptions.reduce(
          (acc, current) => [...acc, current.value],
          []
        ),
        url: `${rawProduct.onlineStoreUrl}?variant=${id}`,
        quantityAvailable: edge.node.quantityAvailable,
        metafields: cleanUpMetafields(edge.node.metafields),
      }
    }),
    metafields,
    isDiscounted: rawProduct.variants?.edges.some((variantEdge) => {
      const variant = variantEdge.node
      const originalPrice = parseFloat(variant.priceV2.amount)
      const compareAtPrice = parseFloat(variant.compareAtPriceV2?.amount)
      return compareAtPrice > originalPrice
    }),
  }
}

/**
 * Makes variant data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for product.
 * @returns {Object|null} The processed product data.
 */
function cleanUpVariantData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawVariant = rawData.node
  const id = stripGID(rawVariant.id)

  return {
    id,
    title: rawVariant.title,
    available: rawVariant.availableForSale,
    price: normalizePrice(rawVariant.priceV2.amount),
    compareAtPrice: normalizePrice(rawVariant.compareAtPriceV2?.amount),
    currencyCode: rawVariant.priceV2.currencyCode,
    availableForSale: rawVariant.availableForSale,
    sku: rawVariant.sku,
    image: { src: rawVariant?.image?.url },
    options: rawVariant.selectedOptions,
    url: `${rawVariant.product.onlineStoreUrl}?variant=${id}`,
    quantityAvailable: rawVariant.quantityAvailable,
    metafields: cleanUpMetafields(rawVariant.metafields),
    product: {
      ...rawVariant.product,
      id: stripGID(rawVariant.product.id),
      swatchValues: rawVariant.product.options.reduce((acc, current) => {
        if (['color', 'material'].includes(current.name.toLowerCase())) {
          return [...acc, ...current.values]
        }
        return acc
      }, []),
      options: rawVariant.product.options,
      variants: rawVariant.product.variants.edges.map((_) => ({
        id: stripGID(_.node.id),
        available: _.node.availableForSale,
        image: { src: _.node.image?.url },
        options: _.node.selectedOptions.reduce(
          (acc, current) => [...acc, current.value],
          []
        ),
        url: `${rawVariant.product.onlineStoreUrl}?variant=${stripGID(
          _.node.id
        )}`,
        price: normalizePrice(_.node.priceV2.amount),
        compareAtPrice: normalizePrice(_.node.compareAtPriceV2?.amount),
        metafields: cleanUpMetafields(_.node.metafields),
      })),
    },
  }
}

/**
 * Process the search data response.
 * @param {Object} rawData - The raw search data.
 * @returns {Array<Object>} The list of processed product data.
 */
function cleanUpSearchData(rawData) {
  if (!rawData) {
    return null
  }
  return rawData.products.edges.map((product) => cleanUpProductData(product))
}

/**
 * Makes collection data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawCollection = rawData.node

  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: rawCollection.products?.edges
      .map((edge) => {
        return cleanUpProductData(edge)
      })
      .filter((product) => __THEME_GLOBALS__.showUnpublishedProducts || !!product.url), // remove products not published to online store by default
    metafields: cleanUpMetafields(rawCollection.metafields),
  }
}

/**
 * Makes collection variant override metafield data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for collection.
 * @returns {Object|null} The processed collection data.
 */
function cleanUpCollectionVariantsFromMetafieldData(rawData) {
  if (!rawData || !rawData.node) {
    return null
  }

  const rawCollection = rawData.node
  const cleanMetafieldData = cleanUpMetafields(rawCollection.metafields)
  return {
    id: stripGID(rawCollection.id),
    title: rawCollection.title,
    handle: rawCollection.handle,
    description: rawCollection.descriptionHtml,
    updatedAt: new Date(rawCollection.updatedAt),
    publishedAt: new Date(rawCollection.publishedAt),
    image: rawCollection.image,
    products: cleanMetafieldData?.custom.customVariantsOverride,
  }
}

/**
 * Makes metaobject data from GraphQL workable with JS.
 * @param {Object} rawData - The GraphQL response for metaobject.
 * @returns {Object|null} The processed metaobject data.
 */
function cleanUpMetaobjectData(rawData) {
  if (!rawData || !rawData.node || !Array.isArray(rawData.node.fields)) {
    console.error('Invalid data structure')
    return {}
  }

  const fieldsArray = rawData.node.fields
  const transformedData = {}

  fieldsArray.forEach((field) => {
    let value = field.value
    if (isStringifiedArray(value)) {
      try {
        value = JSON.parse(value)
      } catch (e) {
        console.warn(`Could not parse JSON for key ${field.key}`, e)
      }
    }
    transformedData[field.key] = value
  })

  const { handle, id, type } = rawData.node
  const result = {
    ...transformedData,
    handle,
    id: stripGID(id),
    type,
  }
  return convertKeysToCamelCase(result)
}

/**
 * Groups an array of product objects, based on style group collection.
 *
 * @param {Array} products - The products to group.
 * @returns {Array} - List of product groups.
 */
const groupProducts = (products) => {
  return products.reduce((acc, product) => {
    const collectionKey = product.metafields.style_group?.collection
    const existingGroup = acc.find(
      (group) => group.collection === collectionKey
    )
    if (existingGroup) {
      existingGroup.products.push(product)
      if (product.minPrice < existingGroup.minPrice) {
        existingGroup.minPrice = product.minPrice
      }
      if (product.maxPrice > existingGroup.maxPrice) {
        existingGroup.maxPrice = product.maxPrice
      }
      if (product.publishedAt.getTime() > existingGroup.published.getTime()) {
        existingGroup.published = product.publishedAt
      }
      if (product.createdAt.getTime() > existingGroup.created.getTime()) {
        existingGroup.created = product.createdAt
      }
    } else {
      acc.push({
        styleGroup: product.metafields?.style_group?.number,
        title: product.title.split('|')[0].trim(),
        vendor: product.vendor,
        collection: collectionKey,
        products: [product],
        minPrice: product.minPrice,
        maxPrice: product.maxPrice,
        published: product.publishedAt,
        created: product.createdAt,
      })
    }
    return acc
  }, [])
}

export {
  StorefrontClient,
  getProductRecommendations,
  cleanUpProductData,
  cleanUpCollectionData,
  cleanUpCollectionVariantsFromMetafieldData,
  groupProducts,
}
